import type { Locale } from 'date-fns';
import arEG from 'date-fns/locale/ar-EG/index.js';
import enGB from 'date-fns/locale/en-GB/index.js';
import fr from 'date-fns/locale/fr/index.js';
import is from 'date-fns/locale/is/index.js';
import pl from 'date-fns/locale/pl/index.js';
import ru from 'date-fns/locale/ru/index.js';
import uz from 'date-fns/locale/uz/index.js';
import { useEffect, useState } from 'react';

import { company } from '~/company/Company';
import { getCookieLang } from '~/utils/cookies';

/**
 * Locales are imported one file at a time, never from `date-fns/locale`.
 *
 * That barrel is CJS and re-exports all 94 locales, which Rollup cannot
 * tree-shake: importing a single name from it puts 660 KiB of minified locale
 * data into the vendor chunk and 118 KB on the wire. Nothing enforces this
 * automatically — one `import { de } from 'date-fns/locale'` anywhere in the
 * app brings all of it back.
 *
 * The `/index.js` suffix is also load-bearing: without it the SSR build fails
 * with ERR_UNSUPPORTED_DIR_IMPORT under Node ESM.
 */
const LOCALES = {
  en: enGB,
  ar: arEG,
  is,
  pl,
  fr,
  ru,
  uz,
};

const DEFAULT_DATE_FORMAT = 'MMMM d';
const NUMERIC_DATE_FORMAT = 'dd.MM.yyyy';
const NUMERIC_DATE_LOCALES = ['ru', 'uz'];

export const useDateLang = (): { locale: Locale; dateFormat: string } => {
  const [dateLang, setDateLang] = useState(
    LOCALES[getCookieLang() as keyof typeof LOCALES] ??
      LOCALES[company.config.lang as keyof typeof LOCALES] ??
      enGB,
  );
  const currentLang = getCookieLang();

  useEffect(() => {
    setDateLang(
      LOCALES[currentLang as keyof typeof LOCALES] ??
        LOCALES[company.config.lang as keyof typeof LOCALES] ??
        enGB,
    );
  }, [currentLang]);

  const dateFormat = NUMERIC_DATE_LOCALES.includes(dateLang.code ?? '')
    ? NUMERIC_DATE_FORMAT
    : DEFAULT_DATE_FORMAT;

  return { locale: dateLang, dateFormat };
};
